/**
 * @param {boolean|string|number} ar
 * @returns {boolean}
 */
export function isProductArEnabled(ar) {
  return ar === true || ar === "true" || ar === 1 || ar === "1";
}

/**
 * Parse ar_link: GLB and USDZ separated by &, order-independent.
 * @param {string} arLink
 * @returns {{ glb: string, usdz: string }}
 */
export function parseArLink(arLink) {
  if (!arLink || typeof arLink !== "string") {
    return { glb: "", usdz: "" };
  }

  const parts = arLink.split("&").map((part) => part.trim()).filter(Boolean);
  let glb = "";
  let usdz = "";

  for (const part of parts) {
    const lower = part.toLowerCase();
    if (!glb && lower.includes(".glb")) {
      glb = part;
    } else if (!usdz && lower.includes(".usdz")) {
      usdz = part;
    }
  }

  return { glb, usdz };
}

/**
 * @param {string} url
 * @returns {string}
 */
export function normalizeArModelUrl(url) {
  if (!url || typeof url !== "string") return url;

  try {
    const parsed = new URL(url);
    if (parsed.protocol === "http:") {
      parsed.protocol = "https:";
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * Build same-origin load candidates from ar_link (first match wins).
 * @param {string} url
 * @returns {string[]}
 */
export function getArModelLoadSources(url) {
  if (!url || typeof url !== "string") return [];
  if (url.startsWith("/")) return [url];

  const normalized = normalizeArModelUrl(url);

  try {
    const parsed = new URL(normalized);
    const sources = [];
    const proxied = `/api/proxy-3d?url=${encodeURIComponent(normalized)}`;

    // Rewrites are faster than the API proxy route — try them first.
    if (parsed.hostname === "smartbathroom.ir") {
      sources.push(`/api-media${parsed.pathname}${parsed.search}`);
    }

    if (parsed.hostname === "smartbathroom.ir") {
      sources.push(proxied);
    }

    sources.push(normalized);

    return [...new Set(sources.filter(Boolean))];
  } catch {
    return [normalized];
  }
}

/**
 * Best same-origin path for model-viewer src.
 * @param {string} url
 * @returns {string}
 */
export function getModelViewerSrc(url) {
  const sources = getArModelLoadSources(url);
  return sources[0] || url;
}

/**
 * Primary same-origin URL for model-viewer src.
 * @param {string} url
 * @returns {string}
 */
export function proxyArModelUrl(url) {
  const sources = getArModelLoadSources(url);
  return sources[0] || url;
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Resolve ar_link path to an absolute HTTPS URL (required for Scene Viewer / Quick Look).
 * @param {string} url
 * @returns {string}
 */
export function getAbsoluteArModelUrl(url) {
  if (!url || typeof url !== "string") return "";

  const normalized = normalizeArModelUrl(url);
  const proxied = proxyArModelUrl(normalized);

  if (proxied.startsWith("http://") || proxied.startsWith("https://")) {
    return proxied;
  }

  if (typeof window !== "undefined" && proxied.startsWith("/")) {
    return `${window.location.origin}${proxied}`;
  }

  return proxied;
}

/**
 * Open native AR viewer on mobile (Scene Viewer on Android, Quick Look on iOS).
 * @param {{ glb: string, usdz: string }} links
 */
export function openProductAr({ glb, usdz }) {
  if (typeof window === "undefined") return;

  if (isIOS()) {
    const iosUrl = usdz ? getAbsoluteArModelUrl(usdz) : "";
    if (!iosUrl) return;

    const anchor = document.createElement("a");
    anchor.setAttribute("rel", "ar");
    anchor.href = iosUrl;
    anchor.style.display = "none";

    const img = document.createElement("img");
    anchor.appendChild(img);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    return;
  }

  const androidUrl = glb ? getAbsoluteArModelUrl(glb) : "";
  if (!androidUrl) return;

  const fallback = encodeURIComponent(window.location.href);
  const intentUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(androidUrl)}&mode=ar_preferred#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=${fallback};end;`;

  window.location.assign(intentUrl);
}
